/**
 * Judgment Index Page
 *
 * The home for Judgment Index profiles in a workspace. A certified practitioner
 * records each person's score sheet result (manually or by CSV upload), reviews
 * individual profiles, sees the team-level judgment composition, and reads
 * prioritization guidance derived from the group's judgment mix.
 *
 * Accessible via Reports → Judgment Index in the sidebar.
 *
 * Advisory only: nothing here weights, gates, or alters any participant's input,
 * scores, or rankings — it sits alongside the cognitive-bias layer.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Scale, Plus, Upload, Users, UserSquare, Lightbulb, Info, Loader2, FileDown } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useJudgmentIndexStore } from '../../../stores/prioritization/judgmentIndexStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { JudgmentIndexProfileCard } from '../../../components/prioritization/judgment/JudgmentIndexProfileCard';
import { JudgmentProfileForm } from '../../../components/prioritization/judgment/JudgmentProfileForm';
import { JudgmentAdvisoryPanel } from '../../../components/prioritization/judgment/JudgmentAdvisoryPanel';
import { aggregateTeam, balanceStatus, overallScore } from '../../../lib/judgmentIndexModel';
import { parseJudgmentCsv } from '../../../lib/judgmentIndexCsv';
import type { JudgmentIndexProfile, JudgmentBalance, ScoreBand } from '../../../types/prioritization/judgmentIndex';
import { BAND_META, BALANCE_META } from '../../../types/prioritization/judgmentIndex';

type Tab = 'people' | 'team' | 'guidance';

// CSV import template — generated client-side (repo gitignores *.csv).
const TEMPLATE_CSV = [
  'name,email,assessed_on,p1_intrinsic,p1_extrinsic,p1_systemic,p2_intrinsic,p2_extrinsic,p2_systemic,bal_people,bal_work,bal_ideas,bal_esteem,bal_concept,bal_image,judgment_type,judgment_type_name',
  'Anthony Gonzalez,anthony@example.com,2022-12-12,10,7,16,21,13,11,-2,-3,-3,-1,-7,8,4,Counseling/Coaching/Mentoring',
  'Ada Lovelace,ada@example.com,2026-01-15,14,12,20,18,10,15,-1,2,-2,3,-4,6,7,Leadership/Executive',
  '',
].join('\n');

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'judgment-index-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

const IES_MAX = 25;

const BAND_BAR: Record<ScoreBand, string> = {
  strong:     'bg-[var(--green)]',
  good:       'bg-[var(--slate)]',
  attention:  'bg-[var(--mustard)]',
  developing: 'bg-[var(--mustard)]',
  needs_work: 'bg-[var(--coral)]',
};

export function JudgmentIndexPage() {
  const { profiles, isLoading, fetchProfiles, createProfile, createMany, updateProfile, deleteProfile } =
    useJudgmentIndexStore();
  const userId = useUserStore((s) => s.userId);

  const [tab, setTab] = useState<Tab>('people');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<JudgmentIndexProfile | null>(null);
  const [importNote, setImportNote] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const aggregate = useMemo(() => aggregateTeam(profiles), [profiles]);

  function openCreate() { setEditing(null); setFormOpen(true); }
  function openEdit(p: JudgmentIndexProfile) { setEditing(p); setFormOpen(true); }

  async function handleDelete(p: JudgmentIndexProfile) {
    if (!window.confirm(`Remove ${p.subjectName}'s Judgment Index profile?`)) return;
    await deleteProfile(p.id);
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportNote(null);
    try {
      const text = await file.text();
      const { rows, errors } = parseJudgmentCsv(text);
      if (rows.length === 0) { setImportNote(errors[0] ?? 'No rows could be imported.'); return; }
      const result = await createMany(rows, userId || null);
      if (result.error) {
        setImportNote(result.error);
      } else {
        const skipped = errors.length > 0 ? ` (${errors.length} row(s) skipped)` : '';
        setImportNote(`Imported ${result.created} profile${result.created === 1 ? '' : 's'}${skipped}.`);
      }
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const actions = (
    <>
      <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
      <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors">
        <FileDown className="h-4 w-4" /> Template
      </button>
      <button type="button" onClick={() => fileRef.current?.click()} disabled={importing} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] disabled:opacity-50 transition-colors">
        {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        Import CSV
      </button>
      <button type="button" onClick={openCreate} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-[var(--slate)] hover:bg-[var(--slate)] shadow-sm shadow-indigo-200 transition-all">
        <Plus className="h-4 w-4" /> Record profile
      </button>
    </>
  );

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'people',   label: 'People',                 icon: UserSquare },
    { id: 'team',     label: 'Team Composition',        icon: Users      },
    { id: 'guidance', label: 'Prioritization Guidance', icon: Lightbulb  },
  ];

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <PageHeader
        icon={<Scale className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Judgment Index"
        subtitle="Hartman judgment profiles for your people — recorded by a certified practitioner, read alongside the prioritization work."
        actions={actions}
      />

      {importNote && (
        <div className="mb-4 flex items-start gap-2 border border-[var(--slate)] bg-[var(--slate-dim)] px-3 py-2 text-sm text-[var(--text-sec)]">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{importNote}</span>
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-[var(--rule)] mb-6">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active ? 'border-[var(--slate)] text-[var(--text-ter)]' : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)]'
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {isLoading && profiles.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-[var(--text-ter)]">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading profiles…
        </div>
      ) : profiles.length === 0 ? (
        <EmptyState onAdd={openCreate} onImport={() => fileRef.current?.click()} />
      ) : tab === 'people' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {profiles.map((p) => (
            <JudgmentIndexProfileCard key={p.id} profile={p} onEdit={openEdit} onDelete={handleDelete} />
          ))}
        </div>
      ) : tab === 'team' ? (
        <TeamComposition aggregate={aggregate} />
      ) : (
        <JudgmentAdvisoryPanel profiles={profiles} />
      )}

      {formOpen && (
        <JudgmentProfileForm
          existing={editing}
          onClose={() => setFormOpen(false)}
          onSubmit={(input) =>
            editing ? updateProfile(editing.id, input) : createProfile(input, userId || null)
          }
        />
      )}
    </div>
  );
}

function TeamComposition({
  aggregate,
}: {
  aggregate: ReturnType<typeof aggregateTeam>;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Profiles" value={String(aggregate.profileCount)} />
        <StatCard label="Work Side (avg)" value={String(overallScore(aggregate.workSideMean, { intrinsic: 0, extrinsic: 0, systemic: 0 }))} />
        <StatCard label="Self Side (avg)" value={String(overallScore({ intrinsic: 0, extrinsic: 0, systemic: 0 }, aggregate.selfSideMean))} />
      </div>

      {/* IES score averages */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <h3 className="text-base font-semibold text-[var(--text-pri)] mb-1">Average primary IES scores</h3>
        <p className="text-xs text-[var(--text-ter)] mb-4">Mean across all recorded profiles. Band zones: Strong ≥ 18, Good ≥ 7, Attention ≥ 4.</p>
        <div className="space-y-2">
          {aggregate.iesStats.map((stat) => {
            const pct = Math.max(2, Math.min(100, (stat.meanScore / IES_MAX) * 100));
            const { label: bandLabel } = BAND_META[stat.band];
            return (
              <div key={stat.key}>
                <div className="flex items-center justify-between text-sm mb-0.5">
                  <span className="text-[var(--text-sec)]">{stat.label}</span>
                  <span className="text-[var(--text-ter)] tabular-nums">
                    {stat.meanScore}
                    <span className="text-[var(--text-ter)] ml-1.5">{bandLabel}</span>
                    {stat.lowCount > 0 && <span className="text-[var(--coral)] ml-1.5">· {stat.lowCount} low</span>}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-[var(--hover-tint)] overflow-hidden">
                  <div className={`h-full rounded-full ${BAND_BAR[stat.band]}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Balance meter averages */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <h3 className="text-base font-semibold text-[var(--text-pri)] mb-1">Average Balance of Judgment</h3>
        <p className="text-xs text-[var(--text-ter)] mb-4">Mean across profiles. Proper balance: −5 to +5.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
          {(Object.keys(aggregate.balanceMean) as (keyof JudgmentBalance)[]).map((k) => {
            const val = aggregate.balanceMean[k];
            const status = balanceStatus(val);
            const pct = ((val + 25) / 50) * 100;
            const markerLeft = Math.max(1, Math.min(99, pct));
            const color = status === 'balanced' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : status === 'over' ? 'text-[var(--mustard)]' : 'text-[var(--coral)]';
            return (
              <div key={k} className="py-1">
                <div className="flex items-center justify-between text-sm mb-0.5">
                  <span className="text-[var(--text-sec)]">{BALANCE_META[k].label}</span>
                  <span className={`tabular-nums font-medium ${color}`}>{val > 0 ? `+${val}` : val}</span>
                </div>
                <div className="relative h-1.5 w-full rounded-full bg-[var(--hover-tint)]">
                  <div className="absolute inset-y-0 left-[50%] w-px bg-[var(--rule-bright)]" />
                  <div
                    className={`absolute top-0 h-full w-1.5 rounded-full -translate-x-1/2 ${
                      status === 'balanced' ? 'bg-[var(--green)]' : status === 'over' ? 'bg-[var(--mustard)]' : 'bg-[var(--coral)]'
                    }`}
                    style={{ left: `${markerLeft}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Balance summary */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb className="h-4 w-4 text-[var(--text-ter)]" />
          <h3 className="text-base font-semibold text-[var(--text-pri)]">Balance read</h3>
        </div>
        <p className="text-sm text-[var(--text-sec)]">{aggregate.balanceSummary}</p>
        <p className="text-xs text-[var(--text-ter)] mt-3">
          See the <span className="font-medium">Prioritization Guidance</span> tab for what this means when comparing and ranking items.
        </p>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
      <div className="text-2xl font-semibold text-[var(--text-pri)] tabular-nums">{value}</div>
      <div className="text-xs uppercase tracking-wide text-[var(--text-ter)] mt-1">{label}</div>
    </div>
  );
}

function EmptyState({ onAdd, onImport }: { onAdd: () => void; onImport: () => void }) {
  return (
    <div className="bg-[var(--bg-raised)] border border-dashed border-[var(--rule)] p-12 text-center">
      <div className="w-12 h-12 bg-[var(--slate-dim)] flex items-center justify-center mx-auto mb-4">
        <Scale className="h-6 w-6 text-[var(--text-ter)]" />
      </div>
      <h3 className="text-base font-semibold text-[var(--text-pri)]">No Judgment Index profiles yet</h3>
      <p className="text-sm text-[var(--text-ter)] mt-1 max-w-md mx-auto">
        Record a person's Judgment Index result to see their judgment profile, the team's
        composition, and guidance for how that judgment mix shapes prioritization.
      </p>
      <div className="flex items-center justify-center gap-2 mt-5">
        <button type="button" onClick={onImport} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors">
          <Upload className="h-4 w-4" /> Import CSV
        </button>
        <button type="button" onClick={onAdd} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-[var(--slate)] hover:bg-[var(--slate)] shadow-sm shadow-indigo-200 transition-all">
          <Plus className="h-4 w-4" /> Record profile
        </button>
      </div>
    </div>
  );
}

export default JudgmentIndexPage;
